import type { FilterOptions, ListAttractionsParams } from '~/types/attractionTypes';

export type LocationFilterType = 'state' | 'province' | 'city';

export type LocationOption = {
  type: LocationFilterType;
  value: string;
  label: string;
  displayLabel: string;
  groupLabel: string;
};

export const LOCATION_TYPE_LABELS: Record<LocationFilterType, string> = {
  state: 'State',
  province: 'Province',
  city: 'City',
};

export function createLocationOption(type: LocationFilterType, value: string): LocationOption {
  const groupLabel = LOCATION_TYPE_LABELS[type];
  return {
    type,
    value,
    label: value,
    displayLabel: `${value} (${groupLabel})`,
    groupLabel,
  };
}

export function getInitialLocationParams(
  sp: URLSearchParams,
): Pick<ListAttractionsParams, 'state' | 'province' | 'city'> {
  const result: Pick<ListAttractionsParams, 'state' | 'province' | 'city'> = {};

  const state = sp.get('state') || undefined;
  if (state) result.state = state;

  const province = sp.get('province') || undefined;
  if (province) result.province = province;

  const city = sp.get('city') || undefined;
  if (city) result.city = city;

  return result;
}

export function buildLocationOptions(filterOptions: FilterOptions | undefined, fallbackCities: Array<string>) {
  const options: Array<LocationOption> = [];
  const seen = new Set<string>();

  const addOptions = (type: LocationFilterType, values: Array<string>) => {
    for (const rawValue of values) {
      const value = rawValue.trim();
      const key = `${type}:${value}`;
      if (!value || seen.has(key)) continue;
      seen.add(key);
      options.push(createLocationOption(type, value));
    }
  };

  addOptions('state', filterOptions?.states ?? []);
  addOptions('province', filterOptions?.provinces ?? []);
  addOptions('city', filterOptions?.cities?.length ? filterOptions.cities : fallbackCities);

  return options;
}

export function getSelectedLocation(filters: Pick<ListAttractionsParams, 'state' | 'province' | 'city'>) {
  if (filters.city) return createLocationOption('city', filters.city);
  if (filters.province) return createLocationOption('province', filters.province);
  if (filters.state) return createLocationOption('state', filters.state);
  return null;
}
